import { Colors } from '@oy/uicore';
import { SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import {
  getCustomMessageCSSProperties,
  getMessageIconUrl,
} from '../../../utilities/inputMessage';
import styles from './MultipleTextInput.module.css';
import { KeyboardKey } from './constants';
import { validateInput } from './helper';
import {
  DataChangeEventHandler,
  InputModifier,
  InputValidator,
  InputValue,
} from './model';

export type MultipleTextInputMessageStyle = {
  type: 'error' | 'warning' | 'custom';
  color?: Colors;
  icon?: 'error';
};

export type MultipleTextInputProps = {
  id?: string;
  data: string[];
  onChange: DataChangeEventHandler;
  modifier?: InputModifier;
  placeholder?: string;
  triggerKeys?: string[];
  validator?: InputValidator;
  disabled?: boolean;
  defaultPillColor?: 'blue' | 'white';
  maxLength?: number;
  messageStyle?: MultipleTextInputMessageStyle | null;
  isOnBlur?: boolean;
  maxData?: number;
  onClick?: () => void;
};

export const MultipleTextInput = ({
  id = 'text-input',
  data,
  onChange,
  onClick,
  modifier,
  placeholder,
  triggerKeys = [KeyboardKey.Comma, KeyboardKey.Enter],
  validator,
  disabled = false,
  defaultPillColor,
  maxLength,
  messageStyle,
  isOnBlur,
  maxData = -1,
}: MultipleTextInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [values, setValues] = useState<InputValue[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const focusTextInput = () => document.getElementById(id)?.focus();

  const setNewValues = (newValues: InputValue[]) => {
    setValues(newValues);
    onChange({
      data: newValues.map(e => e.value),
      invalidValues: newValues.filter(e => !e.isValid).map(e => e.value),
    });
  };

  useEffect(() => {
    if (data.length === 0) return;
    const init = async () => {
      const uniqData = Array.from(new Set(data));
      const initValues: InputValue[] = [];
      setIsValidating(true);
      for (let i = 0; i < uniqData.length; i++) {
        const value = uniqData[i];
        if (value) initValues.push(await validateInput(value, validator));
      }
      setNewValues(initValues);
      setIsValidating(false);
      if (!isOnBlur) {
        focusTextInput();
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addValue = async () => {
    if (!inputRef.current) return;
    const { value } = inputRef.current;
    const modifiedValue = modifier ? modifier(value) : value;
    if (!modifiedValue) return;
    if (maxData !== -1 && data.length > maxData - 1) return;
    if (values.map(e => e.value).includes(modifiedValue)) {
      inputRef.current.value = '';
      return;
    }
    setIsValidating(true);
    setNewValues([...values, await validateInput(modifiedValue, validator)]);
    inputRef.current.value = '';
    setIsValidating(false);
    if (!isOnBlur) {
      focusTextInput();
    }
  };

  const deleteValue = (value: string) => {
    if (!value) return;
    setNewValues(values.filter(e => e.value !== value));
  };

  const keyDownEventHandler = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (triggerKeys.includes(event.key)) {
      event.preventDefault();
      addValue();
    }
  };

  const buildPills = () =>
    values.map((e, index) => (
      <span
        key={index}
        className={classNames(
          styles.pill,
          { [styles.blue]: defaultPillColor === 'blue' },
          {
            [styles.invalid]: !e.isValid,
          },
        )}
      >
        <span className={styles.content}>{e.value}</span>
        <button
          className={styles.remove}
          disabled={disabled}
          onClick={event => {
            if (!disabled) {
              event.preventDefault();
              deleteValue(e.value);
            }
          }}
        >
          <SystemIcon name="close" size={12} />
        </button>
      </span>
    ));

  const getMessageStyle = () => {
    if (!messageStyle) return '';
    switch (messageStyle.type) {
      case 'error':
        return styles.errorInput;
      case 'warning':
        return styles.warningInput;
      case 'custom':
        return styles.customMessageInput;
      default:
        return '';
    }
  };

  const getMessageIcon = () => {
    if (!messageStyle) return '';
    let icon = '';
    if (messageStyle.type === 'error') {
      icon = getMessageIconUrl('error', Colors.RED100);
    } else if (messageStyle.type === 'custom' && messageStyle.icon) {
      icon = getMessageIconUrl(messageStyle.icon, messageStyle.color);
    }
    return icon ? `url("${icon}")` : '';
  };

  const showMessageIcon = () => {
    if (!messageStyle) return false;
    return messageStyle.type === 'error' || !!messageStyle.icon;
  };

  return (
    <div
      className={classNames(styles.container, getMessageStyle(), {
        [styles.inputWithMessageIcon]: showMessageIcon(),
      })}
      style={
        {
          '--message-icon': getMessageIcon(),
          ...getCustomMessageCSSProperties({ ...messageStyle }),
        } as React.CSSProperties
      }
      onClick={focusTextInput}
    >
      {buildPills()}
      <input
        ref={inputRef}
        id={id}
        type="text"
        maxLength={maxLength}
        onBlur={addValue}
        disabled={isValidating || disabled}
        onClick={onClick}
        placeholder={placeholder}
        onKeyDown={keyDownEventHandler}
      />
    </div>
  );
};
